export interface RetryOptions {
  maxAttempts?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  backoffMultiplier?: number;
  retryableErrors?: string[];
  onRetry?: (attempt: number, error: Error) => void;
}

export class RetryableError extends Error {
  constructor(message: string, public readonly originalError?: Error) {
    super(message);
    this.name = 'RetryableError';
  }
}

export class NonRetryableError extends Error {
  constructor(message: string, public readonly originalError?: Error) {
    super(message);
    this.name = 'NonRetryableError';
  }
}

export class RetryUtils {
  private static readonly DEFAULT_OPTIONS: Required<RetryOptions> = {
    maxAttempts: 3,
    initialDelayMs: 1000,
    maxDelayMs: 30000,
    backoffMultiplier: 2,
    retryableErrors: [
      'NetworkError',
      'TimeoutError',
      'ECONNRESET',
      'ETIMEDOUT',
      'ENOTFOUND',
      'AbortError',
    ],
    onRetry: () => {},
  };

  static async withRetry<T>(
    operation: () => Promise<T>,
    options: RetryOptions = {}
  ): Promise<T> {
    const opts = { ...this.DEFAULT_OPTIONS, ...options };
    let lastError: Error;

    for (let attempt = 1; attempt <= opts.maxAttempts; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        if (error instanceof NonRetryableError) {
          throw error;
        }

        const shouldRetry =
          attempt < opts.maxAttempts && this.isRetryableError(lastError, opts);

        if (!shouldRetry) {
          throw lastError;
        }

        const delay = this.calculateDelay(
          attempt,
          opts.initialDelayMs,
          opts.maxDelayMs,
          opts.backoffMultiplier
        );

        opts.onRetry(attempt, lastError);

        await this.sleep(delay);
      }
    }

    throw lastError!;
  }

  private static isRetryableError(
    error: Error,
    options: Required<RetryOptions>
  ): boolean {
    if (error instanceof RetryableError) {
      return true;
    }

    if (error instanceof NonRetryableError) {
      return false;
    }

    const errorMessage = error.message.toLowerCase();
    const errorName = error.name.toLowerCase();

    const retryablePatterns = [
      'network',
      'timeout',
      'econnreset',
      'etimedout',
      'enotfound',
      'abort',
      'fetch failed',
      'failed to fetch',
      'connection',
      '503',
      '502',
      '429',
    ];

    const nonRetryablePatterns = [
      'unauthorized',
      '401',
      '403',
      'forbidden',
      'not found',
      '404',
      'invalid',
      'bad request',
      '400',
    ];

    for (const pattern of nonRetryablePatterns) {
      if (errorMessage.includes(pattern) || errorName.includes(pattern)) {
        return false;
      }
    }

    for (const pattern of retryablePatterns) {
      if (errorMessage.includes(pattern) || errorName.includes(pattern)) {
        return true;
      }
    }

    return options.retryableErrors.some(
      (retryableError) =>
        errorMessage.includes(retryableError.toLowerCase()) ||
        errorName.includes(retryableError.toLowerCase())
    );
  }

  private static calculateDelay(
    attempt: number,
    initialDelay: number,
    maxDelay: number,
    multiplier: number
  ): number {
    const exponentialDelay = initialDelay * Math.pow(multiplier, attempt - 1);
    const jitter = Math.random() * 0.3 * exponentialDelay;
    return Math.min(exponentialDelay + jitter, maxDelay);
  }

  private static sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  static async withTimeout<T>(
    operation: () => Promise<T>,
    timeoutMs: number,
    timeoutMessage = 'Operation timed out'
  ): Promise<T> {
    return Promise.race([
      operation(),
      this.sleep(timeoutMs).then(() => {
        throw new RetryableError(timeoutMessage);
      }),
    ]);
  }

  static async retryWithCircuitBreaker<T>(
    operation: () => Promise<T>,
    options: RetryOptions & { circuitBreakerThreshold?: number } = {}
  ): Promise<T> {
    const threshold = options.circuitBreakerThreshold || 5;
    const failureKey = operation.toString();

    if (!this.circuitBreakerState.has(failureKey)) {
      this.circuitBreakerState.set(failureKey, {
        failures: 0,
        lastFailureTime: 0,
        isOpen: false,
      });
    }

    const state = this.circuitBreakerState.get(failureKey)!;

    if (state.isOpen) {
      const timeSinceLastFailure = Date.now() - state.lastFailureTime;
      const resetTimeout = 60000;

      if (timeSinceLastFailure < resetTimeout) {
        throw new NonRetryableError('Circuit breaker is open');
      } else {
        state.isOpen = false;
        state.failures = 0;
      }
    }

    try {
      const result = await this.withRetry(operation, options);
      state.failures = 0;
      return result;
    } catch (error) {
      state.failures++;
      state.lastFailureTime = Date.now();

      if (state.failures >= threshold) {
        state.isOpen = true;
      }

      throw error;
    }
  }

  private static circuitBreakerState = new Map<
    string,
    { failures: number; lastFailureTime: number; isOpen: boolean }
  >();
}
